import { useEffect, useState } from "react";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useGuardarAjusteDeHomologacion,
  useHomologacion,
} from "@/hooks/useHomologacion";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import type { AjusteDeHomologacion } from "@/lib/homologacion";

/**
 * La escala con que se homologa un comparable contra el predio tasado.
 *
 * QUÉ SE EDITA Y QUÉ NO. La descripción y el porcentaje de cada peldaño. Ni el
 * nombre del ajuste ni el de la categoría: son la identidad con la que el
 * seeder reconoce las filas, y renombrarlos haría que la próxima siembra
 * creara duplicados y el selector ofreciera dos opciones para lo mismo.
 *
 * Y no se agregan ni se quitan peldaños. La escala es el método de tasación,
 * no una preferencia: sumar uno cambia lo que significan todos los demás.
 *
 * UNA TARJETA POR AJUSTE, CON SU PROPIO BOTÓN. Un botón único para las cinco
 * guardaría escalas que nadie tocó, y un error en una dejaría sin saber cuáles
 * se escribieron.
 */
export function MantenedorDeHomologacion() {
  const consulta = useHomologacion();
  const estado = estadoDeConsulta(consulta);

  return (
    <div className="flex flex-col gap-4 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <p className="text-ink-muted text-caption max-w-2xl">
        Cada ajuste corrige una diferencia entre el predio tasado y el comparable. Los
        porcentajes que vinieron sembrados son un <strong>punto de partida</strong> tomado
        del prototipo, no la escala de Consultek: corríjalos acá y quedan fijos —una
        resiembra no los pisa.
      </p>

      {estado.sinConexion && <EstadoSinConexion que="las tablas de homologación" />}

      {estado.hayError && (
        <EstadoDeError
          titulo="No se pudieron cargar las tablas de homologación"
          error={consulta.error}
          onReintentar={consulta.refetch}
          reintentando={consulta.isFetching}
        />
      )}

      {estado.cargando && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-80 w-full rounded-xl" />
          ))}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(consulta.data ?? []).map((ajuste) => (
          <TarjetaDeAjuste key={ajuste.id} ajuste={ajuste} />
        ))}
      </div>
    </div>
  );
}

/**
 * Una tabla: sus peldaños y qué significa.
 *
 * El borrador vive ACÁ y no en la consulta. Con los campos leyendo del caché de
 * la consulta, cada tecla escribiría en el estado compartido y las otras cuatro
 * tarjetas se volverían a dibujar; y al refrescarse la consulta, lo que la
 * persona está escribiendo se perdería sin aviso.
 */
function TarjetaDeAjuste({ ajuste }: { ajuste: AjusteDeHomologacion }) {
  const guardar = useGuardarAjusteDeHomologacion();

  const [descripcion, setDescripcion] = useState(ajuste.descripcion);
  const [porcentajes, setPorcentajes] = useState<Record<number, string>>(() =>
    Object.fromEntries(ajuste.categorias.map((c) => [c.id, String(c.porcentaje)])),
  );

  // Cuando el servidor devuelve lo guardado, el borrador vuelve a ser lo que
  // hay. Sin esto el botón quedaría habilitado para siempre después de guardar:
  // el borrador seguiría difiriendo de un original que ya cambió.
  useEffect(() => {
    setDescripcion(ajuste.descripcion);
    setPorcentajes(
      Object.fromEntries(ajuste.categorias.map((c) => [c.id, String(c.porcentaje)])),
    );
  }, [ajuste]);

  const cambiado =
    descripcion !== ajuste.descripcion ||
    ajuste.categorias.some((c) => Number(porcentajes[c.id]) !== c.porcentaje);

  const enviar = () => {
    guardar.mutate({
      id: ajuste.id,
      cambios: {
        descripcion,
        // La escala ENTERA, no solo lo que cambió: lo que significa un peldaño
        // depende de cuánto valgan los de al lado, así que se lee junta y se
        // guarda junta.
        categorias: ajuste.categorias.map((c) => ({
          id: c.id,
          porcentaje: Number(porcentajes[c.id]),
        })),
      },
    });
  };

  return (
    <section className="border-border flex flex-col gap-3 rounded-xl border-[1.5px] p-4">
      <h2 className="text-ink text-sm font-bold">{ajuste.nombre}</h2>

      <div className="flex flex-col">
        <div className="text-ink-hint flex items-center justify-between gap-2 border-b-[1.5px] pb-1 text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase">
          <span>Categoría</span>
          <span>%</span>
        </div>

        {ajuste.categorias.map((categoria) => (
          <div
            key={categoria.id}
            className="border-border/60 flex items-center justify-between gap-3 border-b py-1.5 last:border-b-0"
          >
            <span className="text-ink text-xs font-semibold">{categoria.categoria}</span>

            <div className="flex items-center gap-1">
              {/* El campo lleva SOLO el número y el "%" va al lado. Adentro
                  obligaría a la persona a escribirlo o a borrarlo, y al
                  servidor a parsear lo que llegue. */}
              <Input
                type="number"
                step="0.5"
                aria-label={`${categoria.categoria}, en ${ajuste.nombre}`}
                className="h-8 w-20 text-right font-mono text-xs tabular-nums"
                value={porcentajes[categoria.id] ?? ""}
                onChange={(e) =>
                  setPorcentajes((actual) => ({
                    ...actual,
                    [categoria.id]: e.target.value,
                  }))
                }
              />
              <span className="text-ink-hint text-xs">%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`descripcion-${ajuste.id}`} className="text-xs">
          Significado de la tabla
        </Label>
        <Textarea
          id={`descripcion-${ajuste.id}`}
          rows={3}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Qué mide este ajuste y cómo se aplica…"
        />
      </div>

      <Button
        size="sm"
        className="self-end"
        // Deshabilitado sin cambios: un botón siempre activo manda una
        // escritura que no cambia nada y deja sin saber si se guardó.
        disabled={!cambiado || guardar.isPending}
        onClick={enviar}
      >
        {guardar.isPending ? "Guardando…" : `Guardar ${ajuste.nombre}`}
      </Button>
    </section>
  );
}
